import React from 'react'
import { SinglePageQuery, HtmlEdge } from 'generated/types/gatsby'
import { Link } from 'gatsby'
import './content-list.scss'

interface ContentListProps {
  readonly data: SinglePageQuery['allHtml']['group']
  readonly activeSection: { sectionName: string; chapterName: string; title: string }
  readonly scrollTop: number
}

const isActive = (section: string, currentSection: string) => section === currentSection

const hasActiveChild = (menu: SinglePageQuery['allHtml']['group'][0]['edges'], title: string) =>
  menu
    .filter(
      (item) =>
        item.node.frontmatter.sectionName != null && item.node.frontmatter.sectionName !== ''
    )
    .some((item) => isActive(item.node.frontmatter.title, title))

const sectionMenu = (menu: HtmlEdge[], scrollTop: number) =>
  // if section has children
  menu.length > 0 && (
    <div className={'la-content-sub-nav-menu la-content-sub-nav__item-menu'}>
      {menu.map(({ node }) => {
        const { slug, title } = node.frontmatter
        return (
          <Link
            to={slug}
            key={`${title}-${slug}`}
            className={'la-content-sub-nav-menu__item'}
            activeClassName="la-content-sub-nav-menu__item_active"
            partiallyActive={true}
            state={{ scrollTop: scrollTop }}
          >
            {title}
          </Link>
        )
      })}
    </div>
  )

const sectionTitle = (
  edges: SinglePageQuery['allHtml']['group'][0]['edges'],
  activeSection: { sectionName: string; chapterName: string; title: string },
  scrollTop: number
) => {
  const menu = edges.reduce((acc, edge) => {
    const key = edge.node.frontmatter.sectionName
    acc[key] = acc[key] || []
    acc[key].push(edge)
    return acc
  }, Object.create(null))

  return Object.keys(menu).map((menuName) => {
    const { slug, sectionName } = menu[menuName].shift().node.frontmatter
    const isActiveGroup = isActive(sectionName, activeSection.sectionName)

    return sectionName != null && sectionName !== '' ? (
      <li
        className={`la-content-sub-nav__item
          ${isActiveGroup ? ' la-content-sub-nav__item_active' : ''}
          ${
            hasActiveChild(menu[menuName], activeSection.title)
              ? ' la-content-sub-nav__item_has-active-child'
              : ''
          }
        `}
        key={sectionName}
      >
        <Link
          to={slug}
          key={slug}
          className={`la-content-sub-nav__item-link${
            menu[menuName].length > 0
              ? isActiveGroup
                ? ' la-content-sub-nav__item-link_expanded'
                : ' la-content-sub-nav__item-link_collapsed'
              : ''
          }`}
          state={{ scrollTop: scrollTop }}
        >
          {sectionName}
        </Link>

        {isActiveGroup && sectionMenu(menu[menuName], scrollTop)}
      </li>
    ) : null
  })
}

export const ContentList = ({ data, activeSection, scrollTop }: ContentListProps) => {
  return (
    <ul className={'la-content-nav'}>
      {data.map((group) => {
        // get first element for chapter title and link
        const { slug, title, chapterName } = group.edges[0].node.frontmatter
        const hasChildren =
          group.edges.filter((edge) => edge.node.frontmatter.sectionName).length > 0

        return (
          <li
            key={slug}
            className={`la-content-nav__item 
              ${
                isActive(chapterName, activeSection.chapterName)
                  ? ' la-content-nav__item_active'
                  : ''
              }
              ${
                hasActiveChild(group.edges, activeSection.title)
                  ? ' la-content-nav__item_has-active-child'
                  : ''
              }
              ${group.edges?.length > 0 ? ' la-icon' : ''}
            `}
          >
            <Link
              to={slug}
              key={slug}
              className={`la-content-nav__item-link${
                hasChildren
                  ? isActive(chapterName, activeSection.chapterName)
                    ? ' la-content-nav__item-link_expanded'
                    : ' la-content-nav__item-link_collapsed'
                  : ''
              }`}
              state={{ scrollTop: scrollTop }}
            >
              {title}
            </Link>
            {hasChildren && isActive(chapterName, activeSection.chapterName) && (
              <ul className={'la-content-sub-nav la-content-nav__item-nav'}>
                {sectionTitle(group.edges, activeSection, scrollTop)}
              </ul>
            )}
          </li>
        )
      })}
    </ul>
  )
}
